import React from "react";
import { Star, MapPin } from "lucide-react";
import { GBP_URL } from "@/lib/contact";
import { useReveal } from "@/hooks/useReveal";
import ReviewLinks from "@/components/site/ReviewLinks";

const GOOGLE_G =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>`
  );

const REVIEWS = [
  {
    name: "Nicodemus Ison",
    meta: "Local Guide · 5 reviews · 31 photos",
    initial: "N",
    color: "#4C6B3A",
    text: "I highly recommend Bluegrass Web Works for anyone who wants to see their business take off! Dave loves what he does and it shows in his designs. Great to work with and very professional.",
  },
  {
    name: "Wendi North",
    meta: "3 reviews · 0 photos",
    initial: "W",
    color: "#8a6d3b",
    text: "I had a idea for my website but Dave was able to totally take my idea and turn it into something off the charts",
  },
  {
    name: "Danny Fennell",
    meta: "Local Guide · 32 reviews · 26 photos",
    initial: "D",
    color: "#5b4b8a",
    text: "Excellent service! Very knowledgeable! Great communication that keeps your project moving forward.",
  },
];

function GoogleStars() {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <figure className="reveal flex flex-col rounded-squircle bg-card border border-border/60 p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-white shrink-0"
          style={{ backgroundColor: r.color }}
        >
          {r.initial}
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold text-foreground text-sm leading-tight">
            {r.name}
          </p>
          <p className="text-xs text-foreground/55 leading-tight mt-0.5">
            {r.meta}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <GoogleStars />
        <span className="text-xs text-foreground/50">5 days ago</span>
        <span className="text-[10px] font-bold tracking-wider border border-foreground/30 text-foreground/70 px-1.5 py-0.5 rounded">
          NEW
        </span>
      </div>

      <blockquote className="flex-1 text-foreground/80 leading-relaxed text-[1.02rem]">
        {r.text}
      </blockquote>
    </figure>
  );
}

export default function GoogleReviews() {
  const sectionRef = useReveal();
  const gbpUrl = GBP_URL;

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 px-5 sm:px-8 bg-secondary/30">
      <div className="max-w-7xl mx-auto">
        <div className="reveal text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={GOOGLE_G} alt="Google logo" className="w-7 h-7" loading="lazy" width="28" height="28" />
            <p className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-chart-2">
              Google Reviews
            </p>
          </div>
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="font-heading font-bold text-2xl text-foreground">
              5.0
            </span>
            <GoogleStars />
            <span className="text-foreground/60 text-sm">3 reviews</span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground text-balance">
            Kentucky businesses recommend Bluegrass Web Works
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
          {REVIEWS.map((r) => (
            <ReviewCard key={r.name} r={r} />
          ))}
        </div>
        <ReviewLinks className="reveal justify-center mt-10" />
        <p className="reveal mt-5 text-center">
          <a
            href={gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent underline underline-offset-2"
          >
            <MapPin size={15} /> View our Google Business Profile
          </a>
        </p>
      </div>
    </section>
  );
}